import { CircleCheck, CirclePause, CircleX, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { FitnessDecision } from '@dru/types';
import { FITNESS_LABEL } from '@dru/types';

/**
 * The four fitness outcomes, each with its own colour AND its own shape.
 *
 * The label is always written out; the colour reinforces it. The icon is the
 * third carrier, for the reader who cannot tell the amber of "restricted" from
 * the orange of "temporarily unfit" — a tick, a warning triangle, a pause and
 * a cross are four different things in any light and to any eye. The marks
 * follow the shared `FitnessDecision` banner the staff consoles use (✓ ! ⏸ ✕),
 * so a worker and their HR team see the same symbol for the same decision.
 */
export const FITNESS_TONE: Record<FitnessDecision, {
  icon: LucideIcon; bg: string; text: string; ring: string; border: string;
}> = {
  FURTHER_EVALUATION: { icon: CirclePause, bg: 'bg-tempUnfit-100', text: 'text-tempUnfit-600', ring: 'ring-tempUnfit-600/25', border: 'border-tempUnfit-600/30' },
  FIT: { icon: CircleCheck, bg: 'bg-fit-100', text: 'text-fit-600', ring: 'ring-fit-600/25', border: 'border-fit-600/30' },
  FIT_WITH_RESTRICTION: { icon: TriangleAlert, bg: 'bg-restricted-100', text: 'text-restricted-600', ring: 'ring-restricted-600/25', border: 'border-restricted-600/30' },
  TEMPORARILY_UNFIT: { icon: CirclePause, bg: 'bg-tempUnfit-100', text: 'text-tempUnfit-600', ring: 'ring-tempUnfit-600/25', border: 'border-tempUnfit-600/30' },
  UNFIT: { icon: CircleX, bg: 'bg-unfit-100', text: 'text-unfit-600', ring: 'ring-unfit-600/25', border: 'border-unfit-600/30' },
};

/** A compact pill: icon + words, on the decision's own ground. */
export function FitnessPill({ decision, className = '' }: { decision: string; className?: string }) {
  const tone = FITNESS_TONE[decision as FitnessDecision];
  const label = FITNESS_LABEL[decision as FitnessDecision];
  if (!tone || !label) return null;
  const Icon = tone.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-semibold ${tone.bg} ${tone.text} ${className}`}>
      <Icon aria-hidden className="h-4 w-4 shrink-0" strokeWidth={2.25} />
      {label}
    </span>
  );
}
